import { FC, useState } from 'react';

import { SwitchIcon } from '../../../assets/icons/index';
import { AppFormattedMessage } from '../../../components/app-formatted-message/index';
import Button from '../../../components/button/index';
import { useScreenSize } from '../../../hooks/index';
import { PublicOwnershipSlice } from '../../../hooks/shared-cap-table/shared-cap-table.types';
import { CapTableGraph } from '../../cap-table/graphs/cap-table-graph';
import { GraphVariant } from '../../cap-table/variables';

type PublicOwnershipChartProps = {
  ownership: PublicOwnershipSlice[];
};

export const PublicOwnershipChart: FC<PublicOwnershipChartProps> = ({ ownership }) => {
  const [variant, setVariant] = useState(GraphVariant.OWNERSHIP);
  const { width } = useScreenSize();
  const isOwnership = variant === GraphVariant.OWNERSHIP;

  // The same thresholds the authorised cap table uses, since the graph is the same component laid
  // out in the same two-column page: below them it stacks the donut over a centred legend.
  const isDesktop = width >= 1344;
  const isTablet = width >= 1024 && width < 1344;

  const switchLabel = () => {
    if (!isDesktop && !isTablet) return <AppFormattedMessage id="SWITCH" />;

    return (
      <AppFormattedMessage
        id={isOwnership ? 'SHARE_PUBLIC_SWITCH_TO_VOTING' : 'SHARE_PUBLIC_SWITCH_TO_OWNERSHIP'}
      />
    );
  };

  return (
    // The same grey shell over a white card the Overall Status block and the app's own graph use.
    <div className="flex h-full min-h-full flex-col rounded-lg bg-gray-50 p-1">
      <div className="flex items-center justify-between gap-2 px-4 py-3">
        <span className="text-[10px] font-bold uppercase leading-4 tracking-[1px] text-gray-700">
          <AppFormattedMessage id={isOwnership ? 'OWNERSHIP_PERCENTAGE' : 'VOTING_RIGHTS'} />
        </span>
        {/* A phone has no room for the full sentence, so it carries the icon and the verb — the
            same trade the app's own graph header makes. */}
        <Button
          className="h-7 w-fit shrink-0 gap-1.5 whitespace-nowrap rounded px-2 text-xs font-[450] text-gray-600"
          onClick={() =>
            setVariant(isOwnership ? GraphVariant.VOTING_RIGHTS : GraphVariant.OWNERSHIP)
          }
          styleType="NONE"
        >
          <SwitchIcon />
          {switchLabel()}
        </Button>
      </div>

      <div className="flex min-h-0 flex-1 flex-col">
        <CapTableGraph
          data={ownership}
          isDesktop={isDesktop}
          isTablet={isTablet}
          variant={variant}
        />
      </div>
    </div>
  );
};
